window.expReady = true;

const SCROLL_LOCK_CLASSES = ['sidebar-opened', 'modal-opened', 'drawer-open'];
const BOKUN_MODAL_ID = 'bokun-modal-container';
const NATIVE_SCROLL_SELECTOR = `.sidebar, .modal, .predictive-search, [data-lenis-prevent], #${BOKUN_MODAL_ID}`;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const MOBILE_QUERY = window.matchMedia('(max-width: 767px)');
const SWIPE_THRESHOLD = 40;
const DRAG_START = 6;
const PARALLAX = 0.14;
const ASIDE_GAP = 24;
const BOKUN_ORIGIN = 'https://widgets.bokun.io';
const RESIZER_PREFIX = '[iFrameSizer]';
const BLOCKED_RESIZER_TYPES = ['scrollTo', 'scrollToOffset', 'inPageLink'];
const HLS_TYPE = 'application/vnd.apple.mpegurl';
const SVG_NS = 'http://www.w3.org/2000/svg';
const gsap = window.gsap;
const CAN_ANIMATE = Boolean(gsap) && !REDUCED_MOTION;
const $html = document.documentElement;

function getHeaderHeight() {
  const $header = document.querySelector('.site-header');
  return $header ? $header.getBoundingClientRect().height : 0;
}

function onIdle(callback) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 300 });
  else window.setTimeout(callback, 1);
}

function getLenisOptions() {
  return {
    duration: 1,
    orientation: 'vertical',
    smoothWheel: true,
    syncTouch: false,
    touchMultiplier: 1.5,
    easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
    infinite: false,
    overscroll: true,
    autoRaf: true,
    allowNestedScroll: true,
    anchors: { offset: -getHeaderHeight() },
    prevent: ($node) => Boolean($node.closest && $node.closest(NATIVE_SCROLL_SELECTOR)),
  };
}

function isScrollLocked() {
  if (SCROLL_LOCK_CLASSES.some((name) => document.body.classList.contains(name))) return true;
  const $modal = document.getElementById(BOKUN_MODAL_ID);
  return Boolean($modal && $modal.getClientRects().length && getComputedStyle($modal).visibility !== 'hidden');
}

function initSmoothScroll() {
  if (REDUCED_MOTION || typeof window.Lenis !== 'function') return null;
  const lenis = new window.Lenis(getLenisOptions());
  let $watchedModal = null;
  const observer = new MutationObserver(() => {
    const $modal = document.getElementById(BOKUN_MODAL_ID);
    if ($modal && $modal !== $watchedModal) {
      $watchedModal = $modal;
      observer.observe($modal, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    if (isScrollLocked()) lenis.stop();
    else lenis.start();
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'], childList: true });
  document.querySelectorAll('[data-lenis-stop]').forEach(($el) => $el.addEventListener('click', () => lenis.stop()));
  document.querySelectorAll('[data-lenis-start]').forEach(($el) => $el.addEventListener('click', () => lenis.start()));
  return lenis;
}

function blockBokunScrollRequests() {
  window.addEventListener(
    'message',
    (event) => {
      if (event.origin !== BOKUN_ORIGIN || typeof event.data !== 'string' || event.data.indexOf(RESIZER_PREFIX) !== 0) return;
      const type = event.data.slice(RESIZER_PREFIX.length).split(':')[3];
      if (BLOCKED_RESIZER_TYPES.includes(type)) event.stopImmediatePropagation();
    },
    true,
  );
}

function initBokunLoader() {
  const $courses = document.querySelector('[data-exp-bokun]');
  if (!$courses) return;
  let loaded = false;
  let $pending = null;
  const isReady = () => Boolean(document.getElementById(BOKUN_MODAL_ID));
  const load = () => {
    if (loaded) return;
    loaded = true;
    const $script = document.createElement('script');
    $script.src = $courses.dataset.expBokun;
    $script.async = true;
    document.body.append($script);
  };
  const replay = (startedAt) => {
    if (!$pending) return;
    if (isReady()) {
      const $button = $pending;
      $pending = null;
      $button.classList.remove('is-loading');
      window.setTimeout(() => $button.click(), 50);
      return;
    }
    if (Date.now() - startedAt < 10000) {
      window.setTimeout(() => replay(startedAt), 100);
      return;
    }
    $pending.classList.remove('is-loading');
    $pending = null;
  };
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      onIdle(load);
    },
    { rootMargin: '300px 0px' },
  );
  observer.observe($courses);
  $courses.querySelectorAll('[data-exp-book]').forEach(($button) => {
    ['pointerenter', 'touchstart', 'focus'].forEach((type) => $button.addEventListener(type, load, { once: true, passive: true }));
    $button.addEventListener('click', (event) => {
      if (isReady()) return;
      event.preventDefault();
      if ($pending) $pending.classList.remove('is-loading');
      $pending = $button;
      $button.classList.add('is-loading');
      load();
      replay(Date.now());
    });
  });
}

function initHeaderOffset() {
  const $header = document.querySelector('.site-header');
  if (!$header) return;
  const update = () => $html.style.setProperty('--exp-header-h', `${Math.round(getHeaderHeight())}px`);
  update();
  new ResizeObserver(update).observe($header);
}

function scrollToElement($target) {
  const offset = -getHeaderHeight();
  if (window.expLenis) {
    window.expLenis.scrollTo($target, { offset });
    return;
  }
  const top = $target.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({ top, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
}

function getPlayableSource($video, src) {
  if (!src || !/\.webm(\?|$)/.test(src) || $video.canPlayType('video/webm; codecs="vp9"')) return src;
  const match = src.match(/\/videos\/c\/o\/v\/([a-f0-9]+)\.webm/);
  if (match && $video.canPlayType(HLS_TYPE)) return `https://cdn.shopify.com/videos/c/vp/${match[1]}/${match[1]}.m3u8`;
  return '';
}

function initHero() {
  document.querySelectorAll('[data-exp-hero]').forEach(($hero) => {
    const $video = $hero.querySelector('[data-exp-video]');
    if ($video && !REDUCED_MOTION) {
      const $play = $hero.querySelector('[data-exp-video-play]');
      const play = () =>
        $video.play().catch((error) => {
          if ($play && error && error.name === 'NotAllowedError') $play.classList.add('is-shown');
        });
      const setSource = () => {
        const src = getPlayableSource($video, MOBILE_QUERY.matches ? $video.dataset.srcMobile : $video.dataset.srcDesktop);
        if (!src || $video.dataset.current === src) return;
        $video.dataset.current = src;
        $video.src = src;
        play();
      };
      $video.addEventListener('playing', () => {
        $video.classList.add('is-playing');
        if ($play) $play.classList.remove('is-shown', 'is-loading');
      });
      if ($play) {
        $play.addEventListener('click', () => {
          $play.classList.add('is-loading');
          $video.play().catch(() => $play.classList.remove('is-loading'));
        });
      }
      setSource();
      MOBILE_QUERY.addEventListener('change', setSource);
    }
    const $accent = $hero.querySelector('.exp-hero__title-accent');
    if ($accent) whenFontsReady(HERO_FONTS, 3000).then(() => {
      if (document.fonts && !document.fonts.check(HERO_FONTS[0])) $accent.classList.add('is-fallback');
      $accent.classList.add('is-ready');
    });
    const $scroll = $hero.querySelector('[data-exp-scroll-next]');
    if (!$scroll) return;
    $scroll.addEventListener('click', () => {
      const $section = $hero.closest('.shopify-section');
      const $target = $section ? $section.nextElementSibling : null;
      if ($target) scrollToElement($target);
    });
  });
}

function splitText($el, mode) {
  const words = $el.textContent.trim().split(/\s+/);
  const $sr = document.createElement('span');
  const $visual = document.createElement('span');
  $sr.className = 'exp-sr';
  $sr.textContent = words.join(' ');
  $visual.setAttribute('aria-hidden', 'true');
  words.forEach((word, index) => {
    const $word = document.createElement('span');
    $word.className = 'exp-split-word';
    if (mode === 'chars') {
      Array.from(word).forEach((char) => {
        const $char = document.createElement('span');
        $char.className = 'exp-split';
        $char.textContent = char;
        $word.append($char);
      });
    } else {
      $word.classList.add('exp-split');
      $word.textContent = word;
    }
    $visual.append($word);
    if (index < words.length - 1) $visual.append(' ');
  });
  $el.replaceChildren($sr, $visual);
  return Array.from($visual.querySelectorAll('.exp-split'));
}

function splitFlow($el) {
  const $sr = document.createElement('span');
  const $visual = document.createElement('span');
  $sr.className = 'exp-sr';
  $sr.textContent = $el.textContent.trim().replace(/\s+/g, ' ');
  $visual.setAttribute('aria-hidden', 'true');
  $visual.append(...Array.from($el.childNodes).map(($node) => $node.cloneNode(true)));
  const walker = document.createTreeWalker($visual, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  const units = [];
  texts.forEach(($text) => {
    const $words = document.createDocumentFragment();
    $text.textContent.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        $words.append(' ');
        return;
      }
      const $word = document.createElement('span');
      $word.className = 'exp-split-word exp-split';
      $word.textContent = part;
      $words.append($word);
      units.push($word);
    });
    $text.replaceWith($words);
  });
  $el.replaceChildren($sr, $visual);
  return units;
}

class AutoSlider {
  constructor($root, { $slides, $drag, $watch, watchShare = 0.25, duration, render, onDrag, onDragCancel }) {
    this.$root = $root;
    this.$watch = $watch || $root;
    this.watchShare = watchShare;
    this.$slides = $slides;
    this.$drag = $drag;
    this.$dotsWrap = $root.querySelector('[data-exp-dots]');
    this.$dots = Array.from($root.querySelectorAll('[data-exp-dot]'));
    this.$ring = $root.querySelector('[data-exp-ring]');
    this.duration = duration;
    this.render = render;
    this.onDrag = onDrag;
    this.onDragCancel = onDragCancel;
    this.index = 0;
    this.timer = null;
    this.inView = false;
    this.pointerId = null;
    this.dragging = false;
    this.$dots.forEach(($dot) => $dot.addEventListener('click', () => this.go(Number($dot.dataset.expDot))));
    this._bindDrag();
    this._watchVisibility();
    this._updateDots();
  }

  get count() {
    return this.$slides.length;
  }

  go(target, direction) {
    if (this.count < 2) return;
    const next = (target + this.count) % this.count;
    if (next === this.index) {
      this._startTimer();
      return;
    }
    const from = this.index;
    this.index = next;
    this._updateDots();
    this.render(from, next, direction || (next > from ? 1 : -1));
    this._startTimer();
  }

  next() {
    this.go(this.index + 1, 1);
  }

  prev() {
    this.go(this.index - 1, -1);
  }

  _updateDots() {
    this.$dots.forEach(($dot, i) => {
      $dot.classList.toggle('is-done', i < this.index);
      $dot.classList.toggle('is-current', i === this.index);
      if (i === this.index) $dot.setAttribute('aria-current', 'true');
      else $dot.removeAttribute('aria-current');
    });
    if (this.$dotsWrap) this.$dotsWrap.style.setProperty('--exp-dot', this.index);
  }

  _startTimer() {
    if (this.timer) this.timer.kill();
    this.timer = null;
    if (!gsap || REDUCED_MOTION || this.count < 2 || !this.$ring) return;
    this.timer = gsap.fromTo(
      this.$ring,
      { strokeDashoffset: 100 },
      { strokeDashoffset: 0, autoRound: false, duration: this.duration, ease: 'none', onComplete: () => this.next() },
    );
    if (!this.inView || document.hidden || this.dragging) this.timer.pause();
  }

  _syncTimer() {
    if (!this.timer) {
      if (this.inView) this._startTimer();
      return;
    }
    if (this.inView && !document.hidden && !this.dragging) this.timer.resume();
    else this.timer.pause();
  }

  _watchVisibility() {
    new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= this.watchShare - 0.01;
        this._syncTimer();
      },
      { threshold: [0, this.watchShare] },
    ).observe(this.$watch);
    document.addEventListener('visibilitychange', () => this._syncTimer());
  }

  _bindDrag() {
    if (!this.$drag) return;
    this.$drag.addEventListener('pointerdown', (event) => this._onPointerDown(event));
    this.$drag.addEventListener('pointermove', (event) => this._onPointerMove(event));
    this.$drag.addEventListener('pointerup', (event) => this._onPointerUp(event));
    this.$drag.addEventListener('pointercancel', (event) => this._onPointerCancel(event));
    this.$drag.addEventListener('dragstart', (event) => event.preventDefault());
  }

  _onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dx = 0;
  }

  _onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    if (!this.dragging) {
      if (Math.abs(dx) < DRAG_START || Math.abs(dx) < Math.abs(dy)) return;
      this.dragging = true;
      try {
        this.$drag.setPointerCapture(event.pointerId);
      } catch (error) {
      }
      this.$drag.classList.add('is-dragging');
      this._syncTimer();
    }
    this.dx = dx;
    if (this.onDrag) this.onDrag(dx, this.index);
  }

  _onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    const wasDragging = this.dragging;
    const dx = this.dx;
    this._endDrag();
    if (!wasDragging) return;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx < 0) this.next();
      else this.prev();
      return;
    }
    if (this.onDragCancel) this.onDragCancel(this.index);
    this._syncTimer();
  }

  _onPointerCancel(event) {
    if (event.pointerId !== this.pointerId) return;
    const wasDragging = this.dragging;
    this._endDrag();
    if (!wasDragging) return;
    if (this.onDragCancel) this.onDragCancel(this.index);
    this._syncTimer();
  }

  _endDrag() {
    this.pointerId = null;
    this.dragging = false;
    this.$drag.classList.remove('is-dragging');
  }
}

const ABOUT_FONTS = ['700 52px the-seasons', '300 34px the-seasons', '500 18px "IBM Plex Sans"', '500 12px "IBM Plex Sans"'];
const HERO_FONTS = ['300 120px the-seasons'];
const FONT_WAIT = 2500;

function whenFontsReady(fonts, wait = FONT_WAIT) {
  if (!document.fonts || !document.fonts.load) return Promise.resolve();
  const loads = Promise.all(fonts.map((font) => document.fonts.load(font))).catch(() => {});
  return Promise.race([loads, new Promise((resolve) => window.setTimeout(resolve, wait))]);
}

function getDuration($root) {
  return parseFloat(getComputedStyle($root).getPropertyValue('--exp-duration')) || 6;
}

function setSlideHidden($slide, hidden) {
  $slide.classList.toggle('is-current', !hidden);
  if (hidden) $slide.setAttribute('aria-hidden', 'true');
  else $slide.removeAttribute('aria-hidden');
}

function initMark($mark) {
  if (!$mark || !CAN_ANIMATE) return null;
  const $defs = document.createElementNS(SVG_NS, 'defs');
  const key = `exp-bar-${Math.random().toString(36).slice(2, 8)}`;
  const bars = Array.from($mark.querySelectorAll('.exp-about__bar-wrap')).map(($wrap, i) => {
    const $path = $wrap.querySelector('path');
    const box = $path.getBBox();
    const $clip = document.createElementNS(SVG_NS, 'clipPath');
    const $rect = document.createElementNS(SVG_NS, 'rect');
    $clip.id = `${key}-${i}`;
    $rect.setAttribute('x', box.x - 2);
    $rect.setAttribute('y', box.y - 2);
    $rect.setAttribute('width', 0);
    $rect.setAttribute('height', box.height + 4);
    $clip.append($rect);
    $defs.append($clip);
    $wrap.setAttribute('clip-path', `url(#${$clip.id})`);
    return { $path, $rect, x: box.x - 2, width: box.width + 4, fromLeft: i % 2 === 0, lit: false };
  });
  $mark.prepend($defs);
  $mark.classList.add('is-clipped');
  return bars;
}

function setBar(bar, lit, delay) {
  bar.lit = lit;
  const edge = bar.fromLeft ? bar.x : bar.x + bar.width;
  const shift = bar.fromLeft ? -18 : 18;
  gsap.killTweensOf([bar.$rect, bar.$path]);
  if (lit) {
    if (Number(bar.$rect.getAttribute('width')) < 0.5) {
      gsap.set(bar.$rect, { attr: { x: edge } });
      gsap.set(bar.$path, { x: shift });
    }
    gsap.to(bar.$rect, { attr: { x: bar.x, width: bar.width }, duration: 1.1, delay, ease: 'expo.out' });
    gsap.to(bar.$path, { x: 0, duration: 1.3, delay, ease: 'expo.out' });
    return;
  }
  gsap.to(bar.$rect, { attr: { x: edge, width: 0 }, duration: 0.7, delay, ease: 'power3.in' });
  gsap.to(bar.$path, { x: shift, duration: 0.7, delay, ease: 'power3.in' });
}

function initAbout() {
  document.querySelectorAll('[data-exp-about]').forEach(($root) => {
    const $slides = Array.from($root.querySelectorAll('[data-exp-slide]'));
    const $litBars = Array.from($root.querySelectorAll('.exp-about__bar--lit'));
    const bars = initMark($root.querySelector('[data-exp-mark]'));
    const units = $slides.map(($slide) =>
      CAN_ANIMATE
        ? Array.from($slide.querySelectorAll('[data-exp-smoke]')).flatMap(($el) => ($el.dataset.expSmoke === 'glyph' ? [$el] : splitText($el, $el.dataset.expSmoke)))
        : [],
    );
    let timeline = null;

    const setBars = (index) => {
      if (!bars) {
        $litBars.forEach(($bar, i) => $bar.classList.toggle('is-lit', i <= index));
        return;
      }
      const hide = bars.filter((bar, i) => i > index && bar.lit).reverse();
      const show = bars.filter((bar, i) => i <= index && !bar.lit);
      hide.forEach((bar, n) => setBar(bar, false, n * 0.06));
      show.forEach((bar, n) => setBar(bar, true, n * 0.14 + (hide.length ? 0.25 : 0)));
    };

    const render = (from, to, direction) => {
      if (timeline) timeline.progress(1).kill();
      setBars(to);
      const $from = $slides[from];
      const $to = $slides[to];
      $from.classList.remove('is-current');
      setSlideHidden($to, false);
      if (!CAN_ANIMATE) {
        setSlideHidden($from, true);
        return;
      }
      gsap.set($to, { x: 0, opacity: 1 });
      timeline = gsap.timeline({
        onComplete: () => {
          setSlideHidden($from, true);
          gsap.set([$from, ...units[from]], { clearProps: 'all' });
        },
      });
      timeline
        .to($from, { x: direction > 0 ? '-=40' : '+=40', duration: 1, ease: 'power2.in' }, 0)
        .to(
          units[from],
          {
            opacity: 0,
            filter: 'blur(10px)',
            x: () => -direction * gsap.utils.random(20, 60),
            y: () => gsap.utils.random(-16, 6),
            duration: 0.8,
            ease: 'power2.in',
            stagger: { each: 0.012, from: direction > 0 ? 'start' : 'end' },
          },
          0,
        )
        .fromTo(
          units[to],
          {
            opacity: 0,
            filter: 'blur(14px)',
            x: () => direction * gsap.utils.random(16, 48),
            y: () => gsap.utils.random(-6, 14),
          },
          { opacity: 1, filter: 'blur(0px)', x: 0, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.018 },
          0.45,
        );
    };

    const $body = $root.querySelector('.exp-about__body');
    const fontsReady = whenFontsReady(ABOUT_FONTS);

    const $lead = $root.querySelector('[data-exp-about-intro]');
    if ($lead) {
      const leadUnits = CAN_ANIMATE
        ? Array.from($lead.querySelectorAll('[data-exp-smoke]')).flatMap(($el) =>
            $el.dataset.expSmoke === 'flow' ? splitFlow($el) : splitText($el, $el.dataset.expSmoke),
          )
        : [];
      const leadIn = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          leadIn.disconnect();
          fontsReady.then(() => {
            $lead.classList.add('is-ready');
            if (!CAN_ANIMATE) return;
            gsap.fromTo(
              leadUnits,
              { opacity: 0, filter: 'blur(14px)', x: () => gsap.utils.random(16, 48), y: () => gsap.utils.random(-6, 14) },
              { opacity: 1, filter: 'blur(0px)', x: 0, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.018 },
            );
          });
        },
        { rootMargin: '0px 0px -10% 0px' },
      );
      leadIn.observe($lead);
    }

    const intro = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        intro.disconnect();
        fontsReady.then(() => {
          if ($body) $body.classList.add('is-ready');
          setBars(slider.index);
          if (!CAN_ANIMATE) return;
          gsap.fromTo(
            units[slider.index],
            { opacity: 0, filter: 'blur(14px)', x: () => gsap.utils.random(16, 48), y: () => gsap.utils.random(-6, 14) },
            { opacity: 1, filter: 'blur(0px)', x: 0, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.018 },
          );
        });
      },
      { rootMargin: '0px 0px -15% 0px' },
    );
    intro.observe($body || $root);

    const slider = new AutoSlider($root, {
      $slides,
      $drag: $root.querySelector('[data-exp-pillars]') || $root.querySelector('[data-exp-slides]'),
      $watch: $body,
      watchShare: 0.9,
      duration: getDuration($root),
      render,
      onDrag: (dx, index) => {
        if (!CAN_ANIMATE) return;
        const pull = Math.min(Math.abs(dx) / 220, 1);
        gsap.set($slides[index], { x: dx * 0.3, opacity: 1 - pull * 0.75, filter: `blur(${(pull * 10).toFixed(2)}px)` });
      },
      onDragCancel: (index) => {
        if (!CAN_ANIMATE) return;
        gsap.to($slides[index], { x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.6, ease: 'power3.out', clearProps: 'transform,opacity,filter' });
      },
    });
    if (!bars) setBars(slider.index);
  });
}

function initReviews() {
  document.querySelectorAll('[data-exp-reviews]').forEach(($root) => {
    const $track = $root.querySelector('[data-exp-track]');
    const $slides = Array.from($root.querySelectorAll('[data-exp-slide]'));
    if (!$track || !$slides.length) return;
    let index = 0;
    const getOffset = (i) => -($slides[i].offsetLeft - $slides[0].offsetLeft);
    const place = (i, animate) => {
      if (!gsap) {
        $track.style.transform = `translateX(${getOffset(i)}px)`;
        return;
      }
      gsap.killTweensOf($track);
      if (animate && !REDUCED_MOTION) gsap.to($track, { x: getOffset(i), duration: 1.1, ease: 'expo.out' });
      else gsap.set($track, { x: getOffset(i) });
    };
    const render = (from, to) => {
      index = to;
      $slides.forEach(($slide, i) => setSlideHidden($slide, i !== to));
      place(to, true);
    };
    new AutoSlider($root, {
      $slides,
      $drag: $track,
      duration: getDuration($root),
      render,
      onDrag: (dx) => {
        if (gsap) gsap.set($track, { x: getOffset(index) + dx });
      },
      onDragCancel: () => place(index, true),
    });
    new ResizeObserver(() => place(index, false)).observe($track);
  });
}

function initCourses() {
  document.querySelectorAll('[data-exp-courses]').forEach(($root) => {
    const $tabs = Array.from($root.querySelectorAll('[data-exp-city-tab]'));
    const $courses = Array.from($root.querySelectorAll('[data-exp-course]'));
    const setCity = (city, animate) => {
      if (!$tabs.some(($tab) => $tab.dataset.expCityTab === city)) return;
      const changed = $root.dataset.expCity !== city;
      $root.dataset.expCity = city;
      $tabs.forEach(($tab) => $tab.setAttribute('aria-pressed', String($tab.dataset.expCityTab === city)));
      if (!changed) return;
      const url = new URL(window.location.href);
      url.searchParams.set('city', city);
      window.history.replaceState(window.history.state, '', url);
      const $visible = $courses.filter(($course) => $course.dataset.city === city);
      if (animate && CAN_ANIMATE) {
        gsap.fromTo($visible, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', clearProps: 'opacity,transform' });
      }
    };
    setCity($root.dataset.expCity, false);
    $tabs.forEach(($tab) => $tab.addEventListener('click', () => setCity($tab.dataset.expCityTab, true)));
    $courses.forEach(($course) => {
      initPlans($course);
      initPhotos($course);
      initAside($course);
    });
    initCardVideos($root);
  });
}

function initPlans($course) {
  const $plans = Array.from($course.querySelectorAll('[data-exp-plan]'));
  const $price = $course.querySelector('[data-exp-price]');
  if (!$plans.length) return;
  let timeline = null;
  const getChecked = () => $plans.find(($plan) => $plan.getAttribute('aria-checked') === 'true') || $plans[0];
  const applyPrice = () => {
    if ($price) $price.textContent = getChecked().dataset.price;
  };
  const select = ($plan) => {
    if ($plan === getChecked()) return;
    $plans.forEach(($item) => {
      $item.setAttribute('aria-checked', String($item === $plan));
      $item.tabIndex = $item === $plan ? 0 : -1;
    });
    if (!$price || !CAN_ANIMATE) {
      applyPrice();
      return;
    }
    if (timeline) timeline.kill();
    timeline = gsap
      .timeline()
      .to($price, { opacity: 0, y: -4, duration: 0.15, ease: 'power2.in', onComplete: applyPrice })
      .fromTo($price, { y: 4 }, { opacity: 1, y: 0, duration: 0.35, ease: 'power3.out' });
  };
  $plans.forEach(($plan, i) => {
    $plan.tabIndex = $plan === getChecked() ? 0 : -1;
    $plan.addEventListener('click', () => select($plan));
    $plan.addEventListener('keydown', (event) => {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const $next = $plans[(i + step + $plans.length) % $plans.length];
      $next.focus();
      select($next);
    });
  });
}

function initCardVideos($root) {
  const $videos = Array.from($root.querySelectorAll('[data-exp-card-video]'));
  if (!$videos.length || REDUCED_MOTION) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target: $video, isIntersecting }) => {
        if (!isIntersecting) {
          $video.pause();
          return;
        }
        if (!$video.getAttribute('src')) $video.src = $video.dataset.src;
        $video.play().catch(() => {});
      });
    },
    { rootMargin: '200px 0px' },
  );
  $videos.forEach(($video) => observer.observe($video));
}

function initPhotos($course) {
  const $photos = Array.from($course.querySelectorAll('[data-exp-photo]'));
  if (!$photos.length) return;
  const setActive = ($active) => $photos.forEach(($photo) => $photo.classList.toggle('is-active', $photo === $active));
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target);
      });
    },
    { rootMargin: '-50% 0px -50% 0px' },
  );
  $photos.forEach(($photo) => observer.observe($photo));
}

function initAside($course) {
  const $aside = $course.querySelector('[data-exp-aside]');
  if (!$aside) return;
  const update = () => {
    if (MOBILE_QUERY.matches || !$aside.offsetHeight) {
      $aside.style.removeProperty('--exp-aside-top');
      return;
    }
    const header = getHeaderHeight();
    const top = Math.min(header + ASIDE_GAP, window.innerHeight - $aside.offsetHeight - ASIDE_GAP);
    $aside.style.setProperty('--exp-aside-top', `${Math.round(top)}px`);
  };
  update();
  new ResizeObserver(update).observe($aside);
  window.addEventListener('resize', update);
}

function reveal($item, delay) {
  if ($item.dataset.expReveal === 'photo') {
    const $img = $item.querySelector('img');
    gsap.to($item, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, delay, ease: 'expo.out' });
    if ($img) gsap.to($img, { scale: 1, duration: 1.8, delay, ease: 'expo.out' });
    return;
  }
  gsap.fromTo($item, { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 1.2, delay, ease: 'expo.out' });
}

function initParallax() {
  if (REDUCED_MOTION) return;
  const items = Array.from(document.querySelectorAll('[data-exp-parallax]'), ($box) => ({
    $box,
    $img: $box.querySelector('img'),
    anchor: $box.dataset.expParallax === 'end' ? 1 : 0.5,
    visible: true,
  })).filter((item) => item.$img);
  if (!items.length) return;
  let frame = 0;
  const update = () => {
    frame = 0;
    const viewport = window.innerHeight;
    items.forEach((item) => {
      if (!item.visible) return;
      const rect = item.$box.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));
      const y = (progress - item.anchor) * rect.height * PARALLAX;
      item.$img.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
    });
  };
  const request = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  update();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const item = items.find(({ $box }) => $box === entry.target);
        if (item) item.visible = entry.isIntersecting;
      });
      request();
    },
    { rootMargin: '25% 0px' },
  );
  items.forEach(({ $box }) => observer.observe($box));
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);
}

function initReveal() {
  if (!$html.classList.contains('exp-js')) return;
  if (!CAN_ANIMATE) {
    $html.classList.remove('exp-js');
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach((entry, i) => {
          observer.unobserve(entry.target);
          reveal(entry.target, i * 0.08);
        });
    },
    { rootMargin: '0px 0px -8% 0px' },
  );
  document.querySelectorAll('[data-exp-reveal]').forEach(($item) => observer.observe($item));
}

blockBokunScrollRequests();
window.expLenis = initSmoothScroll();
initHeaderOffset();
initHero();
initParallax();
onIdle(() => {
  initAbout();
  initCourses();
  initBokunLoader();
  initReviews();
  initReveal();
});
